'use client';

import { Header } from '@portals/external-services-header';
import { useState } from 'react';
import {
  TLink,
  TSelectOption,
} from '@portals/external-services-header/lib/_components/header/_types';
import { ECountry, ELanguage } from '@portals/help-center-core';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import classnames from 'classnames/bind';
import { resolveUrl } from '@portal-root/geo-utils';
import { useUpdatePath } from '@/_hooks/use-update-path';
import { HC_IS_PROD } from '@/_constants/env';
import { getValidCountry } from '@/utils/get-valid-country';
import { getValidLocale } from '@/utils/get-valid-locale';
import { useClientEnvContext } from '@/contexts';
import { LANGUAGE_OPTIONS, NOT_TRANSLATABLE_COUNTRIES } from './_constants';
import { TCountryKey } from './_types';
import { getCurrentRegion } from './_utils/get-current-region';
import { getOptionsType } from './_utils/get-options-type';
import { getRegionFlag } from './_utils/get-region-flag';
import { getNavBarItems } from './_utils/get-nav-bar-items';
import { getListFromEnum } from './_utils/get-list-from-enum';
import { Logo } from './components/logo';
import styles from './index.module.scss';

const BLOCK_NAME = 'AppHeader';
const cn = classnames.bind(styles);

type TCountryListItem = Exclude<ECountry, ECountry.AZERBAIJAN>;

// список сортирован исходя из количества материалов
const COUNTRY_LIST = getListFromEnum<TCountryListItem>()([
  ECountry.RUSSIA,
  ECountry.ARMENIA,
  ECountry.BELARUS,
  ECountry.KAZAKHSTAN,
  ECountry.UZBEKISTAN,
  ECountry.KYRGYZSTAN,
  ECountry.CHINA,
  ECountry.GEORGIA,
  ECountry.TAJIKISTAN,
  ECountry.TURKEY,
  ECountry.UNITED_NATIONS,
  ECountry.ETHIOPIA,
]);

const COUNTRY_LIST_GE = [ECountry.GEORGIA];

export const AppHeader = () => {
  const translations = useTranslations('countries');
  const { locale, country } = useParams();
  const { updatePath } = useUpdatePath();
  const {
    basePath,
    instructionUrlRu,
    geZoneProd,
    geZoneStage,
    proWbRu,
    proWbCn,
    currentZone,
  } = useClientEnvContext();
  const [currentRegion, setCurrentRegion] = useState<ECountry>(
    getCurrentRegion(country),
  );
  const isGeDomain = currentZone.endsWith(ECountry.GEORGIA);

  const isTranslatableCountry = (
    countryCode: ECountry,
  ): countryCode is TCountryKey =>
    !NOT_TRANSLATABLE_COUNTRIES.includes(countryCode);

  const availableCountries = isGeDomain
    ? COUNTRY_LIST_GE
    : COUNTRY_LIST.filter(isTranslatableCountry);

  const countryOptions = availableCountries.map((countryValue, index) => ({
    id: String(index + 1),
    title: translations(countryValue),
    value: countryValue,
    dropdownIcon: getRegionFlag(countryValue as ECountry),
    icon: getRegionFlag(countryValue as ECountry),
  }));

  const [countryOption, setCountryOption] = useState<TSelectOption>(
    getOptionsType({ locale: country, options: countryOptions }),
  );

  const onCountyChange = (updatedOption: TSelectOption) => {
    const newCountry = updatedOption.value;

    if (newCountry === country) return;

    if (newCountry === ECountry.GEORGIA) {
      const geHelpCenterPath = `${basePath}/${ELanguage.GEORGIAN}/${ECountry.GEORGIA}`;
      // resolveUrl заменяет базовый RU-хост, но не произвольный домен страны.
      const link = new URL(geHelpCenterPath, instructionUrlRu).toString();
      window?.open(
        resolveUrl(link, HC_IS_PROD ? geZoneProd : geZoneStage),
        '_blank',
      );

      return;
    }

    setCurrentRegion(newCountry as ECountry);
    setCountryOption(updatedOption);
    updatePath({ newCountry: newCountry as unknown as ECountry });
  };

  const currentLanguageOptions = LANGUAGE_OPTIONS[currentRegion];

  const [languageValue, setLanguageValue] = useState<TSelectOption>(
    getOptionsType({ options: currentLanguageOptions, locale }),
  );

  const onLanguageChange = (updatedOption: TSelectOption) => {
    setLanguageValue(updatedOption);
    updatePath({ newLocale: updatedOption.value as unknown as ELanguage });
  };

  const translationsCommon = useTranslations('common');
  const navBarItems = isGeDomain
    ? []
    : getNavBarItems({
        proWbRu,
        proWbCn,
        country: getValidCountry(country as ECountry),
        locale: getValidLocale(locale as ELanguage),
      }).map((item) => ({
        ...item,
        text: translationsCommon(item.text),
      }));

  const menuItems = getNavBarItems({
    proWbRu,
    proWbCn,
    country: getValidCountry(country as ECountry),
    locale: getValidLocale(locale as ELanguage),
  }).map((item) => ({
    ...item,
    title: translationsCommon(item.text),
  }));

  const handleOpenLink = (item: TSelectOption | TLink) => {
    window.open(item.routeName, '_blank');
  };

  const countrySelectParams = {
    value: countryOption?.id,
    options: countryOptions,
    onChange: onCountyChange,
  };

  return (
    <div className={cn(BLOCK_NAME)} id="Portal-header">
      <Header
        countrySelectParams={countrySelectParams}
        createInstance
        languageSelectParams={{
          value: languageValue.id,
          options: currentLanguageOptions,
          onChange: onLanguageChange,
        }}
        links={{
          onClick: handleOpenLink,
          desktop: navBarItems,
          mobile: navBarItems,
        }}
        customLogo={<Logo />}
        menuParams={{
          mobile: {
            onClick: handleOpenLink,
            options: menuItems,
          },
        }}
        variant="default"
      />
    </div>
  );
};
